import React, { useEffect, useState } from 'react';
import {
  IconButton,
  Avatar,
  Box,
  CloseButton,
  Flex,
  HStack,
  VStack,
  Icon,
  useColorModeValue,
  Link,
  Drawer,
  DrawerContent,
  Text,
  useDisclosure,
  BoxProps,
  FlexProps,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList, Img, Center, Divider
} from "@chakra-ui/react";
import { FiHome, FiMenu, FiBell, FiChevronDown, FiLogIn, FiUser, FiCalendar } from 'react-icons/fi';
import { IconType } from 'react-icons';
import {
  MdAppRegistration,
  MdOutlineSportsBasketball
} from 'react-icons/md';
import { FaRegIdCard } from 'react-icons/fa';
import { useAuth } from './auth';
import { getUserFullName, Role, User } from '../models/user';
import { TbFileSymlink } from 'react-icons/tb';
import { Auth } from '../models/auth';
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';
import {
  LINK_MEMBER_PAGE_ROUTE,
  LOGIN_PAGE_ROUTE, MY_ACTIVITIES_PAGE,
  MY_DATA_PAGE_ROUTE,
  MY_INSURANCE_PAGE,
  MY_MEMBERSHIP_CREDENTIAL_PAGE_ROUTE,
  MY_MEMBERSHIP_FEES_PAGE_ROUTE,
  REGISTRATION_PAGE_ROUTE
} from '../constants';
import {getMemberFullName} from "../models/member";
import ColorModeToggle from './color-mode-toggle';
import VersionInfo from './version-info';
import { RiHealthBookLine } from 'react-icons/ri';

interface SidebarHeaderProps {
  content: JSX.Element;
}

interface LinkItemProps {
  name: string;
  icon: IconType;
  link: string;
  isPrivate: boolean;
  isPublicOnly: boolean;
  isPartnerOnly: boolean;
  isAnyUser: boolean;
  isAdminOnly: boolean;
}

interface NavItemProps extends FlexProps {
  label: string;
  icon: IconType;
  link: string;
}

interface SidebarProps extends BoxProps {
  onClose: () => void;
  isOpen: boolean;
  sidebarBg?: string;
}

const LinkItems: LinkItemProps[] = [
  {
    name: 'Iniciar sesión',
    icon: FiLogIn,
    link: LOGIN_PAGE_ROUTE,
    isPrivate: false,
    isPublicOnly: true,
    isAnyUser: false,
    isPartnerOnly: false,
    isAdminOnly: false
  },
  {
    name: 'Registro',
    icon: MdAppRegistration,
    link: REGISTRATION_PAGE_ROUTE,
    isPrivate: false,
    isPublicOnly: true,
    isAnyUser: false,
    isPartnerOnly: false,
    isAdminOnly: false
  },
  {
    name: 'Inicio',
    icon: FiHome,
    link: '/dashboard',
    isPrivate: true,
    isPublicOnly: false,
    isAnyUser: true,
    isPartnerOnly: false,
    isAdminOnly: false
  },
  {
    name: 'Vincular socio',
    icon: TbFileSymlink,
    link: LINK_MEMBER_PAGE_ROUTE,
    isPrivate: true,
    isPublicOnly: false,
    isAnyUser: false,
    isPartnerOnly: false,
    isAdminOnly: false
  },
  {
    name: 'Mis datos',
    icon: FiUser,
    link: MY_DATA_PAGE_ROUTE,
    isPrivate: true,
    isPublicOnly: false,
    isAnyUser: false,
    isPartnerOnly: true,
    isAdminOnly: false
  },
  {
    name: 'Mis cuotas',
    icon: FiCalendar,
    link: MY_MEMBERSHIP_FEES_PAGE_ROUTE,
    isPrivate: true,
    isPublicOnly: false,
    isAnyUser: false,
    isPartnerOnly: true,
    isAdminOnly: false
  },
  {
    name: 'Mi credencial',
    icon: FaRegIdCard,
    link: MY_MEMBERSHIP_CREDENTIAL_PAGE_ROUTE,
    isPrivate: true,
    isPublicOnly: false,
    isAnyUser: false,
    isPartnerOnly: true,
    isAdminOnly: false
  },
  {
    name: 'Mis actividades',
    icon: MdOutlineSportsBasketball,
    link: MY_ACTIVITIES_PAGE,
    isPrivate: true,
    isPublicOnly: false,
    isAnyUser: false,
    isPartnerOnly: true,
    isAdminOnly: false
  },
  {
    name: 'Mi seguro',
    icon: RiHealthBookLine,
    link: MY_INSURANCE_PAGE,
    isPrivate: true,
    isPublicOnly: false,
    isAnyUser: false,
    isPartnerOnly: true,
    isAdminOnly: false
  }
];

function Sidebar(props: SidebarHeaderProps): JSX.Element {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const appBg = useColorModeValue('green.50', 'gray.900');
  const sidebarBg = useColorModeValue(
    'green.600',
    'green.800'
  );

  return (
    <Box minH="100vh" bg={appBg}>
      <SidebarContent
        onClose={() => onClose}
        background={sidebarBg}
        display={{ base: 'none', md: 'block' }}
        fontSize="1.1em"
        isOpen={isOpen}
        sidebarBg={sidebarBg}
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent onClose={onClose} isOpen={isOpen} sidebarBg={sidebarBg} />
        </DrawerContent>
      </Drawer>
      {/* MobileNav */}
      <MobileNav onOpen={onOpen}/>
      <Box ml={{ base: 0, md: 60 }} padding={10}>
        {props.content}
      </Box>
    </Box>
  );
}

const SidebarContent = ({ onClose, isOpen, sidebarBg, ...rest }: SidebarProps): JSX.Element => {
  const [links, setLinks] = useState<JSX.Element[]>([]);
  const authContext = useAuth();
  const user = authContext.user as User;

  useEffect(() => {
    const getLinks = (): JSX.Element[] => {
      return LinkItems.filter((link) => {
        if (
          ((!link.isPrivate && link.isPublicOnly && user == null) ?? false) ||
          ((link.isPrivate && link.isPartnerOnly && user?.selectedMember != null) ??
            false) ||
          ((link.isPrivate && link.isAnyUser && user != null) ?? false) ||
          ((link.isPrivate &&
            !link.isPartnerOnly &&
            user != null &&
            user?.selectedMember == null) ??
            false) ||
          (link.isAdminOnly && user?.role === Role.Admin)
        ) {
          return link;
        }
        return null;
      }).map((link) => {
        return (
          <NavItem fontWeight="semibold"
            key={link.link}
            label={link.name}
            icon={link.icon}
            link={link.link}
            onClick={() => {
              if (isOpen) {
                onClose();
              }
            }}
          >
            {link.name}
          </NavItem>
        );
      });
    };

    setLinks(getLinks());
  }, [user, user?.selectedMember]);

  return (
    <Box
      transition="3s ease"
      background={sidebarBg}
      borderRight="2px"
      borderRightColor="green.600"
      width={{ base: 'full', md: 60 }}
      position="fixed"
      height="full"
      color="white"
      {...rest}
    >
      <Flex
        height="20"
        alignItems="center"
        mx="8"
        justifyContent="space-between"
      >
        <Img boxSize='80px' objectFit='cover' src={'/pacifico.png'}></Img>
        <Text fontSize="2xl" fontFamily="Verdana" fontWeight="bold" color="white">C.A. Pacífico</Text>
        <CloseButton display={{ base: 'flex', md: 'none' }} onClick={onClose} color="white" />
      </Flex>
      <Center><Text fontSize="lg" fontFamily="sloganFont" color="green.100">El primero de la ciudad</Text></Center>
      <Divider borderColor="green.400" my={4} />
      {links}
      <VersionInfo position="sidebar" />
    </Box>
  );
};

const NavItem = ({
  label,
  icon,
  link,
  children,
  ...rest
}: NavItemProps): JSX.Element => {
  return (
    <Link
      as={ReactRouterLink}
      to={link}
      style={{ textDecoration: 'none' }}
      _focus={{ boxShadow: 'none' }}
    >
      <Flex
        align="center"
        p="4"
        mx="4"
        borderRadius="lg"
        role="group"
        cursor="pointer"
        color="green.100"
        fontWeight="medium"
        transition="all 0.2s ease-in-out"
        _hover={{
          bg: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          transform: 'translateX(4px)',
          boxShadow: 'lg'
        }}
        {...rest}
      >
        {icon != null && (
          <Icon
            mr="4"
            fontSize="18"
            color="green.200"
            _groupHover={{
              color: 'white',
              transform: 'scale(1.1)'
            }}
            transition="all 0.2s ease-in-out"
            as={icon}
          />
        )}
        {label}
      </Flex>
    </Link>
  );
};

interface MobileProps extends FlexProps {
  onOpen: () => void;
}

const MobileNav = ({ onOpen, ...rest }: MobileProps): JSX.Element => {
  const authContext: Auth = useAuth();
  const navigate = useNavigate();
  const background = useColorModeValue('white', 'gray.900');
  const borderBottom = useColorModeValue('green.200', 'gray.700');
  const menuList = useColorModeValue('white', 'gray.900');
  const menuListBorder = useColorModeValue('green.200', 'gray.700');
  const user = authContext.user as User;

  if (user == null) {
    return <></>;
  }

  const username = getUserFullName(user);

  const handleLogout = (): void => {
    const signout = async (): Promise<void> => {
      await authContext.logout();
      navigate('/login');
    };

    signout().catch(() => {});
  };

  return (
    <Flex
      ml={{ base: 0, md: 60 }}
      px={{ base: 4, md: 4 }}
      height="20"
      alignItems="center"
      bg={background}
      borderBottomWidth="1px"
      borderBottomColor={borderBottom}
      justifyContent={{ base: 'space-between', md: 'flex-end' }}
      {...rest}
    >
      <IconButton
        display={{ base: 'flex', md: 'none' }}
        onClick={onOpen}
        variant="outline"
        aria-label="open menu"
        icon={<FiMenu />}
      />

      <Text
        display={{ base: 'flex', md: 'none' }}
        fontSize="2xl"
        fontFamily="Verdana"
        fontWeight="bold"
      >
        C.A. Pacífico
      </Text>

      <HStack spacing={{ base: '0', md: '6' }}>
        <ColorModeToggle size="lg" variant="ghost" />
        <Flex alignItems={'center'}>
          <Menu>
            <MenuButton
              py={2}
              transition="all 0.3s"
              _focus={{ boxShadow: 'none' }}
            >
              <HStack>
                <Avatar size={'md'} name={getUserFullName(user)} src={user.selectedMember?.imagePath ?? `/default-profile-picture.jpeg`} />
                <VStack
                  display={{ base: 'none', md: 'flex' }}
                  alignItems="flex-start"
                  spacing="1px"
                  ml="2"
                >
                  <Text fontSize="sm">{username}</Text>
                  <Text fontSize="xs" color="gray.600">
                    Socio N° {user.selectedMember?.memberNumber}
                  </Text>
                </VStack>
                <Box display={{ base: 'none', md: 'flex' }}>
                  <FiChevronDown />
                </Box>
              </HStack>
            </MenuButton>
              <MenuList bg={menuList} borderColor={menuListBorder}>
                {
                  user?.members.length > 1
                    ? (
                      user?.members.map((member) => (
                        <MenuItem key={member.id} minH='48px' isDisabled={user?.selectedMember?.id === member.id} onClick={() => {
                          authContext.linkMember(member);
                          navigate('/dashboard');
                        }}>
                          <Avatar size={'sm'} name={getMemberFullName(member)} src={member.imagePath ?? ''} />
                          <span>{getMemberFullName(member)}</span>
                        </MenuItem>
                      ))
                    )
                    : null
                }
                {
                  user?.members.length > 1
                    ? <MenuDivider />
                    : null
                }
                <MenuItem>
                  <Text onClick={() => { navigate(LINK_MEMBER_PAGE_ROUTE) }}>Vincular otro socio</Text>
                </MenuItem>
                <MenuDivider />
                <MenuItem>
                  <Text onClick={handleLogout}>Cerrar sesión</Text>
                </MenuItem>
              </MenuList>
          </Menu>
        </Flex>
      </HStack>
    </Flex>
  );
};

export default Sidebar;
